import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';

import { DetailsSection } from '@/components/ui/features/in-person-events/DetailsSection/DetailsSection';
import { InPersonHeading } from '@/components/ui/features/in-person-events/Heading/InPersonHeading';
import { PartnerWithUnderline } from '@/components/ui/features/in-person-events/PartnerWithUnderline/PartnerWithUnderline';
import { PurpleGradientSection } from '@/components/ui/features/in-person-events/PurpleGradientSection/PurpleGradientSection';
import { ContactUsSection } from '@/components/ui/features/white-glove-service/ContactUsSection/ContactUsSection';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { JsonLd } from '@/components/ui/shared/seo/JsonLd/JsonLd';
import { AccessFrom } from '@/enums/AccessFrom';
import { queryTestimonials } from '@/queries/testimonals';
import { generateStore } from '@/utils/generateStore';
import { buildBreadcrumbSchema, buildWebPageSchema, getAbsoluteUrl } from '@/utils/structured-data';
import { Hydrate } from '@/utils/swr/Hydrate';

const PAGE_PATH = 'events/in-person-events';
const PAGE_DESCRIPTION =
  'With a track record of over 100 successful in-person conferences, Underline is your go-to partner for creating events that are not only seamless and engaging but also cost-effective. Leveraging decades of expertise in the scientific event sector, we offer flexible solutions tailored to your needs.';
const PAGE_TITLE = 'In-Person Events: The Power of Face-to-Face Interaction';

const InPersonEventsService: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ fallback }) => {
  return (
    <Hydrate fallback={fallback}>
      <Layout accessFrom={AccessFrom.Core} navigationVariant="ghost">
        <NextSeo
          title={PAGE_TITLE}
          description={PAGE_DESCRIPTION}
          canonical={getAbsoluteUrl(PAGE_PATH)}
          openGraph={{
            url: getAbsoluteUrl(PAGE_PATH),
            title: PAGE_TITLE,
            description: PAGE_DESCRIPTION,
          }}
        />
        <JsonLd
          id="events-in-person"
          schema={[
            buildWebPageSchema({
              path: PAGE_PATH,
              name: PAGE_TITLE,
              description: PAGE_DESCRIPTION,
            }),
            buildBreadcrumbSchema([
              { name: 'Home', path: '' },
              { name: 'Events', path: 'events' },
              { name: 'In-Person Events', path: PAGE_PATH },
            ]),
          ]}
        />
        <InPersonHeading />
        <PurpleGradientSection />
        <DetailsSection />
        <PartnerWithUnderline />
        <ContactUsSection />
      </Layout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  await store.fetchResources(queryTestimonials());

  return {
    props: {
      fallback: store.fallback,
    },
  };
};
export default InPersonEventsService;
